import './App.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, UNSAFE_future } from 'react-router-dom';
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth';
import { ImageProvider } from './context/ImageContext';
import Header from './components/Header/Header';
import LandingHeader from './components/Header/LandingHeader';
import Sidebar from './components/Sidebar/Sidebar';
import Main from './components/Sidebar/Main';
import InputDesign from './components/userAccount/userProfile/InputDesign';
import PaymentSuccessful from './components/Stripe/PaymentSuccessful';
import PaymentFailed from './components/Stripe/PaymentFailed';
import MainLandingPage, { use_cases } from './components/landingPage/MainLandingPage';
import LandingPage5 from './components/landingPage/LandingPage5';
import MobileView from './components/MobileView/MobileView';
import UseCases from './components/landingPage/useCases/UseCases';
import PricingPage from './components/landingPage/PricingPage';
import TermsAndConditionsPage from './components/landingPage/TermsAndConditionsPage';
import { fetchAllEditHistory, fetchAllCreateHistory } from './utils/utilities';
import { getUserCredits } from './dbApiFunctions';
import Layout from './components/loginRegister/Layout'
import { auth } from './firebase';


function App() {
  const [selectedEditGroup, setSelectedEditGroup] = useState([{ src: "", alt: "Default edit image" }]);
  const [creditsAvailable, setCreditsAvailable] = useState(0);
  const [editUploadedImages, setEditUploadedImages] = useState([{ src: "https://img.icons8.com/?size=100&id=6RfZ-eZQz0ee&format=png&color=737373", alt: "No Uploaded Image", name: "No Uploaded Image" }]);
  const [brushSize, setBrushSize] = useState(20);
  const [eraserSize, setEraserSize] = useState(20);
  const [activeButton, setActiveButton] = useState('pen');
  const [allCreateHistory, setAllCreateHistory] = useState([]);
  const [allEditHistory, setAllEditHistory] = useState([]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [createPageNumber, setCreatePageNumber] = useState(0);
  const [editPageNumber, setEditPageNumber] = useState(0);
  const [selectedProduct, setSelectedProduct] = useState({ "src": "https://img.icons8.com/?size=100&id=6RfZ-eZQz0ee&format=png&color=737373", "name": "No Product Selected", "refNum": null, alt: "No Product Selected" });
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1020);
  const [createNumberOfImages, setCreateNumberOfImages] = useState(4);
  const [createLoadingCircle, setCreateLoadingCircle] = useState(false);
  const [editNumberOfImages, setEditNumberOfImages] = useState('1');
  const [editLoadingCircle, setEditLoadingCircle] = useState(false);
  const [openSection, setOpenSection] = useState('prompt');

  // Check if screen is small / mobile
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1020);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check if user is logged in
  useEffect(() => {
    const EMAIL = sessionStorage.getItem("email");
    const PASSWORD = sessionStorage.getItem("password");

    // Only attempt authentication if credentials exist and Firebase auth is available
    if (EMAIL && PASSWORD && auth) {
      signInWithEmailAndPassword(auth, EMAIL, PASSWORD)
        .then((userCredential) => {
          setIsLoggedIn(true);
        })
        .catch((error) => {
          // Handle specific error types gracefully
          if (error.code === 'auth/network-request-failed' ||
            error.code === 'auth/too-many-requests' ||
            error.message.includes('ERR_BLOCKED_BY_CLIENT')) {
            console.warn("Authentication request blocked or failed, continuing without login:", error.message);
            // Don't set login state, let user manually log in
          } else {
            console.error("Authentication error:", error);
            // Clear invalid credentials
            sessionStorage.removeItem("email");
            sessionStorage.removeItem("password");
          }
        });
    }
  }, []);

  // Get user credits - only if logged in
  useEffect(() => {
    if (isLoggedIn) {
      const EMAIL = sessionStorage.getItem("email");
      if (EMAIL) {
        getUserCredits(EMAIL).then(response => {
          if (response.status) {
            setCreditsAvailable(response.credits);
          }
        }).catch(error => {
          console.warn("Failed to fetch user credits:", error);
          // Don't break the app if credits can't be fetched
        });
      }
    }
  }, [isLoggedIn]);

  // Fetch all create history - only if logged in
  useEffect(() => {
    if (isLoggedIn) {
      fetchAllCreateHistory(allCreateHistory, setAllCreateHistory, createPageNumber, 'all_createPage');
    }
  }, [createPageNumber, isLoggedIn]);

  // Fetch all edit history - only if logged in
  useEffect(() => {
    if (isLoggedIn) {
      fetchAllEditHistory(allEditHistory, setAllEditHistory, editPageNumber);
    }
  }, [editPageNumber, isLoggedIn]);

  
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      {
        isLoggedIn
          ?
          isMobile ? (<MobileView />) : (
            <ImageProvider>
              <div className="bg-gray-50 min-h-screen flex flex-col">
                <div className="w-full">
                  <Header creditsAvailable={creditsAvailable} />
                </div>
                <Routes>
                  <Route path="/webapp" element={
                    <div className="flex flex-1">
                      <Sidebar
                        editUploadedImages={editUploadedImages}
                        setEditUploadedImages={setEditUploadedImages}
                        brushSize={brushSize}
                        setBrushSize={setBrushSize}
                        eraserSize={eraserSize}
                        setEraserSize={setEraserSize}
                        activeButton={activeButton}
                        setActiveButton={setActiveButton}
                        selectedProduct={selectedProduct}
                        setSelectedProduct={setSelectedProduct}
                        fetchAllCreateHistory={fetchAllCreateHistory}
                        fetchAllEditHistory={fetchAllEditHistory}
                        allCreateHistory={allCreateHistory}
                        setAllCreateHistory={setAllCreateHistory}
                        allEditHistory={allEditHistory}
                        setAllEditHistory={setAllEditHistory}
                        setCreditsAvailable={setCreditsAvailable}
                        setSelectedEditGroup={setSelectedEditGroup}
                        setCreatePageNumber={setCreatePageNumber}
                        setEditPageNumber={setEditPageNumber}
                        createNumberOfImages={createNumberOfImages}
                        editNumberOfImages={editNumberOfImages}
                        setCreateNumberOfImages={setCreateNumberOfImages}
                        setEditNumberOfImages={setEditNumberOfImages}
                        createLoadingCircle={createLoadingCircle}
                        editLoadingCircle={editLoadingCircle}
                        setCreateLoadingCircle={setCreateLoadingCircle}
                        setEditLoadingCircle={setEditLoadingCircle}
                        openSection={openSection}
                        setOpenSection={setOpenSection}
                      />
                      <Main
                        editUploadedImages={editUploadedImages}
                        setEditUploadedImages={setEditUploadedImages}
                        brushSize={brushSize}
                        eraserSize={eraserSize}
                        activeButton={activeButton}
                        allCreateHistory={allCreateHistory}
                        allEditHistory={allEditHistory}
                        setAllEditHistory={setAllEditHistory}
                        createPageNumber={createPageNumber}
                        editPageNumber={editPageNumber}
                        setCreatePageNumber={setCreatePageNumber}
                        setEditPageNumber={setEditPageNumber}
                        creditsAvailable={creditsAvailable}
                        setCreditsAvailable={setCreditsAvailable}
                        selectedEditGroup={selectedEditGroup}
                        setSelectedEditGroup={setSelectedEditGroup}
                        createNumberOfImages={createNumberOfImages}
                        editNumberOfImages={editNumberOfImages}
                        createLoadingCircle={createLoadingCircle}
                        editLoadingCircle={editLoadingCircle}
                        openSection={openSection}
                      />

                    </div>
                  } />

                  <Route path="/profile" element={<InputDesign />} />
                  <Route path="/paymentSuccess" element={<PaymentSuccessful />} />
                  <Route path="/paymentFailed" element={<PaymentFailed />} />
                  <Route path="/use-cases" element={<UseCases useCases={use_cases} />} />
                  <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
                </Routes>
              </div>
            </ImageProvider>
          ) : (
            <Routes>
              <Route path="/webapp" element={<Layout setIsLoggedIn={setIsLoggedIn} />} />
              <Route path="/" element={
                <>
                  <LandingHeader />
                  <MainLandingPage />
                </>
              } />
              <Route path="/use-cases" element={
                <>
                  <LandingHeader />
                  <UseCases useCases={use_cases} />
                  <LandingPage5 />
                </>
              } />
              <Route path="/pricing" element={
                <>
                  <LandingHeader />
                  <PricingPage />
                </>
              } />
              <Route path="/paymentSuccess" element={<PaymentSuccessful />} />
              <Route path="/paymentFailed" element={<PaymentFailed />} />
              <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
            </Routes>
          )
      }
    </Router>
  );
}

export default App;

